import { TableVirtuoso } from "react-virtuoso";
import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
	useReducer,
	useCallback,
	startTransition,
	forwardRef,
} from "react";
import {
	Box,
	CircularProgress,
	TableContainer,
	Paper,
	Table,
	TableHead,
	TableRow,
	TableCell,
	Button,
	LinearProgress,
	Stack,
	Alert,
	TableBody,
	Typography,
	Skeleton,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";
import DateTimeRangePicker from "@wojtekmaj/react-datetimerange-picker";
import "@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css";
import "react-calendar/dist/Calendar.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/en-gb";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("en-gb");

const BASE_COUNT = 100;
const MAX_STAGE = 3;

const initialState = {
	headers: [],
	rowMap: new Map(),
	orderedKeys: [],
	currentStage: null,
	isFetching: false,
	hasFetchedOnce: false, // NEW: track if any fetch completed
	error: null,
};

function reducer(state, action) {
	switch (action.type) {
		case "RESET":
			return { ...initialState };
		case "FETCH_START":
			return {
				...state,
				isFetching: true,
				error: null,
				hasFetchedOnce: false,
			};
		case "FETCH_END":
			return { ...state, isFetching: false, hasFetchedOnce: true };
		case "FETCH_ERROR":
			return {
				...state,
				isFetching: false,
				error: action.error,
				hasFetchedOnce: true,
			};
		case "SET_STAGE":
			return { ...state, currentStage: action.stage };
		case "MERGE_HEADERS": {
			const incoming = action.headers || [];
			if (incoming.length === 0) return state;
			if (state.headers.length === 0)
				return { ...state, headers: incoming };
			const seen = new Set(state.headers.map((h) => h.id));
			const extra = incoming.filter((h) => !seen.has(h.id));
			if (extra.length === 0) return state;
			return { ...state, headers: state.headers.concat(extra) };
		}
		case "MERGE_STAGE_ROWS": {
			const rows = action.rows || [];
			if (rows.length === 0) return state;

			const nextMap = new Map(state.rowMap);
			const A = state.orderedKeys;
			const B = rows.map((r) => r.key);

			for (const r of rows) {
				if (r?.key) nextMap.set(r.key, r);
			}

			// linear merge of two sorted lists A (existing) and B (new)
			const C = [];
			let i = 0,
				j = 0;
			while (i < A.length && j < B.length) {
				const a = A[i];
				const b = B[j];
				if (a === b) {
					C.push(a);
					i++;
					j++;
				} else if (a < b) {
					C.push(a);
					i++;
				} else {
					C.push(b);
					j++;
				}
			}
			while (i < A.length) C.push(A[i++]);
			while (j < B.length) C.push(B[j++]);

			return { ...state, rowMap: nextMap, orderedKeys: C };
		}
		default:
			return state;
	}
}

const Logs = ({ isMobile, vehicleId, chassisNum }) => {
	const [range, setRange] = useState({
		from: dayjs().subtract(1, "hour"),
		to: dayjs(),
	});
	const [pickerValue, setPickerValue] = useState([
		dayjs().subtract(1, "hour").toDate(),
		dayjs().toDate(),
	]);
	const [downloadLoading, setDownloadLoading] = useState(false);

	const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
	const BACKEND_TOKEN = process.env.REACT_APP_BACKEND_TOKEN;

	const [
		{ headers, rowMap, orderedKeys, isFetching, hasFetchedOnce, error },
		dispatch,
	] = useReducer(reducer, initialState);

	const abortRef = useRef(null);
	const debounceRef = useRef(null);

	// NEW: generation token to cancel staged loops safely
	const genRef = useRef(0);

	const validRange = useMemo(
		() =>
			range.from &&
			range.to &&
			dayjs(range.from).isValid() &&
			dayjs(range.to).isValid() &&
			dayjs(range.from).isBefore(range.to),
		[range]
	);

	const buildBaseURL = useCallback(() => {
		const url = new URL(BACKEND_URL);
		url.pathname = `${url.pathname.replace(
			/\/$/,
			""
		)}/vehicles/${vehicleId}/logs/`;
		return url;
	}, [BACKEND_URL, vehicleId]);

	const hardAbort = useCallback(() => {
		try {
			abortRef.current?.abort();
		} catch {}
		abortRef.current = null;
	}, []);

	const resetFeed = useCallback(() => {
		hardAbort();
		genRef.current += 1; // invalidate any in-flight staged loop
		dispatch({ type: "RESET" });
	}, [hardAbort]);

	const mergeHeaders = useCallback((incoming) => {
		if (!Array.isArray(incoming) || incoming.length === 0) return;
		dispatch({ type: "MERGE_HEADERS", headers: incoming });
	}, []);

	const mergeStageRows = useCallback((rows) => {
		if (!Array.isArray(rows) || rows.length === 0) return;
		startTransition(() => {
			dispatch({ type: "MERGE_STAGE_ROWS", rows });
		});
	}, []);

	const fetchStage = useCallback(
		async (stage, controller, { fromISO, toISO, gen }) => {
			const url = buildBaseURL();
			url.searchParams.set("from", fromISO);
			url.searchParams.set("to", toISO);
			url.searchParams.set("stage", String(stage));
			url.searchParams.set("base_count", String(BASE_COUNT));

			const resp = await fetch(url.toString(), {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${BACKEND_TOKEN}`,
					Accept: "application/json",
				},
				signal: controller.signal,
			});
			if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
			const payload = await resp.json();
			const envelope = payload?.data ?? payload;

			if (gen !== genRef.current || controller.signal.aborted) return;

			if (Array.isArray(envelope?.headers))
				mergeHeaders(envelope.headers);
			if (Array.isArray(envelope?.rows)) mergeStageRows(envelope.rows);

			dispatch({ type: "SET_STAGE", stage });
		},
		[BACKEND_TOKEN, buildBaseURL, mergeHeaders, mergeStageRows]
	);

	const fetchAllStages = useCallback(
		async ({ fromISO, toISO } = {}) => {
			if (!validRange) return;
			dispatch({ type: "FETCH_START" });

			// Set up a fresh controller and a local generation snapshot
			const controller = new AbortController();
			abortRef.current = controller;
			const myGen = ++genRef.current; // NEW: increment to mark a new fetch cycle

			try {
				for (let s = 0; s <= MAX_STAGE; s++) {
					// break if a newer cycle started or this controller was aborted
					if (myGen !== genRef.current || controller.signal.aborted)
						break;
					await fetchStage(s, controller, {
						fromISO,
						toISO,
						gen: myGen,
					});
				}
			} catch (e) {
				if (e?.name === "AbortError") return; // silent on manual abort
				console.error(e);
				dispatch({
					type: "FETCH_ERROR",
					error: e?.message || "Failed to fetch logs.",
				});
				return;
			} finally {
				if (myGen === genRef.current) {
					dispatch({ type: "FETCH_END" });
				}
			}
		},
		[fetchStage, validRange]
	);

	useEffect(() => {
		if (headers.length === 0 && orderedKeys.length === 0 && validRange) {
			resetFeed();
			fetchAllStages({
				fromISO: dayjs(range.from).toISOString(),
				toISO: dayjs(range.to).toISOString(),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onPickerChange = (val) => {
		if (!Array.isArray(val) || val.length !== 2) return;
		setPickerValue(val);

		if (debounceRef.current) window.clearTimeout(debounceRef.current);

		// Abort immediately on change to stop current network work
		hardAbort();
		genRef.current += 1; // invalidate any ongoing staged loop

		debounceRef.current = window.setTimeout(() => {
			const [fromD, toD] = val;
			const from = dayjs(fromD || undefined);
			const to = dayjs(toD || undefined);
			if (!from.isValid() || !to.isValid() || !from.isBefore(to)) return;

			setRange({ from, to });
			// Reset state and fetch fresh with new generation
			dispatch({ type: "RESET" });
			fetchAllStages({
				fromISO: from.toISOString(),
				toISO: to.toISOString(),
			});
		}, 300);
	};

	const getDownloadUrl = async () => {
		const res = await fetch(
			`${BACKEND_URL}vehicles/${vehicleId}/logs/download-ticket/`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${BACKEND_TOKEN}`,
				},
				body: JSON.stringify({
					from: dayjs(range.from).toISOString(),
					to: dayjs(range.to).toISOString(),
				}),
			}
		);
		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			throw new Error(
				err.detail || `Ticket request failed (${res.status})`
			);
		}
		const json = await res.json();
		return json.data?.url;
	};

	function ensureHiddenIframe(id = "csv-download-iframe") {
		let iframe = document.getElementById(id);
		if (!iframe) {
			iframe = document.createElement("iframe");
			iframe.id = id;
			iframe.style.display = "none";
			document.body.appendChild(iframe);
		}
		return iframe;
	}

	const handleDownload = async () => {
		setDownloadLoading(true);
		try {
			const url = await getDownloadUrl();
			const iframe = ensureHiddenIframe();
			iframe.src = url;
			setTimeout(() => setDownloadLoading(false), 1200);
		} catch (e) {
			console.error(e);
			setDownloadLoading(false);
		}
	};

	const virtuosoComponents = useMemo(
		() => ({
			Scroller: forwardRef(function Scroller(props, ref) {
				return (
					<TableContainer
						component={Paper}
						elevation={1}
						sx={{ mt: 1 }}
						{...props}
						ref={ref}
					/>
				);
			}),
			Table: (props) => (
				<Table
					{...props}
					stickyHeader
					size="small"
					aria-label="vehicle logs"
					sx={{ borderCollapse: "separate", tableLayout: "fixed" }}
				/>
			),
			TableHead: forwardRef(function TableHeadRef(props, ref) {
				return <TableHead {...props} ref={ref} />;
			}),
			TableRow: (props) => <TableRow {...props} />,
			TableBody: forwardRef(function TableBodyRef(props, ref) {
				return <TableBody {...props} ref={ref} />;
			}),
		}),
		[]
	);

	const colWidths = useMemo(
		() => headers.map((_, i) => (i === 0 ? 200 : 140)),
		[headers]
	);

	const cellSx = (i) => ({
		width: colWidths[i],
		maxWidth: colWidths[i],
		overflow: "visible",
		whiteSpace: "nowrap",
		textOverflow: "unset",
		p: 1,
		lineHeight: 1.4,
		verticalAlign: "middle",
	});

	const renderCellValue = (v) => {
		if (typeof v === "boolean") return v ? "True" : "False";
		if (v == null) return "";
		if (typeof v === "object") return JSON.stringify(v);
		return String(v);
	};

	// Skeleton table for loading state
	const LoadingTable = () => {
		const cols = Math.max(headers.length, 4);
		const rows = isMobile ? 6 : 10;
		return (
			<TableContainer component={Paper} elevation={1} sx={{ mt: 1 }}>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							{(headers.length
								? headers
								: Array.from({ length: cols }, (_, i) => ({
										id: i,
										label: "",
								  }))
							).map((h, i) => (
								<TableCell key={h.id ?? i} sx={cellSx(i)}>
									{headers.length ? (
										<strong>{h.label}</strong>
									) : (
										<Skeleton width={120} />
									)}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{Array.from({ length: rows }).map((_, r) => (
							<TableRow key={r}>
								{Array.from({ length: cols }).map((__, c) => (
									<TableCell key={c} sx={cellSx(c)}>
										<Skeleton />
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		);
	};

	const hasRows = orderedKeys.length > 0;
	const showSkeleton = isFetching && !hasRows && !hasFetchedOnce;
	const showTable = hasRows; // render progressively
	const showEmpty = !isFetching && hasFetchedOnce && validRange && !hasRows;

	return (
		<Box>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				mb={1}
			>
				<Typography variant="subtitle1">
					Logs{" "}
					<Typography variant="caption">{chassisNum}</Typography>
				</Typography>
			</Stack>

			<Box
				sx={{
					position: "relative",
					zIndex: 1,
					overflow: "visible",
				}}
			>
				<LocalizationProvider
					dateAdapter={AdapterDayjs}
					adapterLocale="en-gb"
				>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={2}
						alignItems="center"
						mb={2}
					>
						<DateTimeRangePicker
							onChange={onPickerChange}
							value={pickerValue}
							clearIcon={null}
							disableClock
							maxDate={new Date()}
						/>
					</Stack>
				</LocalizationProvider>
			</Box>

			<Button
				onClick={handleDownload}
				variant="outlined"
				disabled={!validRange || downloadLoading}
				startIcon={!downloadLoading ? <DownloadIcon /> : undefined}
				sx={{ position: "relative", minWidth: 140 }}
			>
				{downloadLoading ? (
					<CircularProgress size={20} sx={{ position: "absolute" }} />
				) : (
					"Download"
				)}
			</Button>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{/* Loading skeletons only until first rows arrive */}
			{showSkeleton && <LoadingTable />}

			{/* Empty state only after a completed fetch with no rows */}
			{showEmpty && (
				<Paper elevation={1} sx={{ mt: 1, p: 4, textAlign: "center" }}>
					<Typography variant="body1">
						No data for selected range.
					</Typography>
				</Paper>
			)}
			{/* Progressive table: render as soon as any stage has produced rows */}
			{showTable && (
				<TableVirtuoso
					style={{ height: isMobile ? 420 : 640 }}
					data={orderedKeys}
					components={virtuosoComponents}
					fixedHeaderContent={() => (
						<TableRow>
							{headers.length > 0 ? (
								headers.map((h, i) => (
									<TableCell key={h.id} sx={cellSx(i)}>
										<strong>{h.label}</strong>
									</TableCell>
								))
							) : (
								<TableCell sx={cellSx(0)}>—</TableCell>
							)}
						</TableRow>
					)}
					itemContent={(index, k) => {
						const row = rowMap.get(k) || {};
						return headers.map((h, i) => (
							<TableCell
								key={h.id}
								sx={cellSx(i)}
								title={renderCellValue(row[h.id])}
							>
								{renderCellValue(row[h.id])}
							</TableCell>
						));
					}}
				/>
			)}

			{/* Keep the thin progress bar during staged loads */}
			{isFetching && (
				<Box sx={{ mt: 1 }}>
					<LinearProgress />
				</Box>
			)}
		</Box>
	);
};

export default Logs;
